import axios from 'axios';
import { API_BASE_URL, TOKEN_KEY } from '../constants/config';

/**
 * Axios instance pre-configured with base URL and interceptors
 */
const httpClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
  withCredentials: true,
});

/**
 * Request interceptor — injects JWT token into every outgoing request
 */
httpClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => { throw error; }
);

/**
 * Strip the bearer token and the request body from a failed request before it reaches callers.
 *
 * Callers routinely `console.error('…', error)` the whole AxiosError, and its `config` carries the
 * `Authorization: Bearer …` header added above plus the request payload (emails, names, and for
 * account flows, passwords). This runs only at the final rethrow, after the 401 retry below has
 * used the original config, so nothing downstream loses anything it reads.
 * `error.response.config` is the same object, so it is covered too.
 */
export function redactRequestSecrets(error) {
  const config = error?.config;
  if (!config) return error;

  if (config.headers?.Authorization) {
    config.headers.Authorization = '[redacted]';
  }
  if (config.data !== undefined) {
    config.data = '[redacted]';
  }
  return error;
}

/**
 * Response interceptor — on a 401, refreshes via Keycloak's own token and retries the
 * request; if Keycloak can't refresh (session gone, or never initialized), logs out through
 * the same canonical redirect the deliberate and forced-logout paths already use.
 *
 * keycloakService is imported dynamically (not at module scope) because it transitively
 * imports shared/lib/tenant, which itself imports this file — a static top-level import here
 * would create an import cycle. The dynamic import only resolves once a 401 actually occurs,
 * well after the module graph has finished loading, so it never observes the cycle.
 */
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { updateToken, getToken, getKeycloak, logoutToLogoutPage } =
        await import('@features/auth/services/keycloakService');

      try {
        await updateToken();
        const token = getToken();
        if (!token) {
          throw new Error('No token after Keycloak refresh');
        }

        localStorage.setItem(TOKEN_KEY, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;

        return httpClient(originalRequest);
      } catch (_refreshError) {
        localStorage.removeItem(TOKEN_KEY);

        let keycloakInstance = null;
        try {
          keycloakInstance = getKeycloak();
        } catch {
          keycloakInstance = null;
        }
        await logoutToLogoutPage(keycloakInstance);

        throw _refreshError;
      }
    }

    throw redactRequestSecrets(error);
  }
);

export default httpClient;
